/**
 * "Instagram post" picker on a social placement (Results panel). Linking the
 * placement to the post / reel / story that went out copies its numbers into
 * the placement's results and, when there's no proof yet, its pictures
 * (every carousel card) into the proof of delivery.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { fmtShort } from '../../lib/dates';
import { CampaignAsset } from '../../lib/types';
import {
  InstagramMedia, useInstagramLink, useInstagramMedia, useInstagramStoryPicture,
} from '../../lib/api/assets';
import { errorDetail } from '../../lib/api/shopBanners';

interface Props {
  asset: CampaignAsset;
  /** Called with the placement as saved after a link / refresh, so the form
   *  shows the fetched numbers and proof (and doesn't overwrite them on Save). */
  onLinked: (asset: CampaignAsset) => void;
}

const fmtWhen = (iso: string | null) => (iso ? fmtShort(iso.slice(0, 10)) : '');
const timeOf = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');

const Thumb = ({ m }: { m: InstagramMedia }) => {
  const saved = useInstagramStoryPicture(m.id, m.saved_story);
  const src = m.saved_story ? saved.data : m.thumbnail;
  return (
    <div style={{ width: 44, height: 44, flex: '0 0 44px', borderRadius: 6, overflow: 'hidden', background: 'var(--bg-subtle)' }}>
      {src && <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
    </div>
  );
};

const InstagramLink = ({ asset: initial, onLinked }: Props) => {
  // Kept here after a link / refresh: the form's own `asset` prop stays as
  // opened, so its other fields aren't reset.
  const [asset, setAsset] = useState(initial);
  const changed = useRef(false);
  const qc = useQueryClient();
  useEffect(() => () => {
    if (changed.current) qc.invalidateQueries({ queryKey: ['assets'] });
  }, [qc]);
  const [picking, setPicking] = useState(false);
  const [kindFilter, setKindFilter] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const link = useInstagramLink();
  const list = useInstagramMedia(picking);

  const linked = asset.instagram_media_id;

  const run = (mediaId: string | null) => {
    setMessage(null);
    link.mutate({ id: asset.id, mediaId }, {
      onSuccess: (saved) => {
        changed.current = true;
        setAsset(saved);
        onLinked(saved);
        setPicking(false);
      },
      onError: (err) => setMessage(errorDetail(err) ?? 'Something went wrong with Instagram.'),
    });
  };

  const notConnected = (list.error as { response?: { status?: number } } | null)?.response?.status === 503;
  // A story placement opens on stories; otherwise everything.
  const rows = (list.data ?? []).filter((m) => !kindFilter || m.kind === kindFilter);

  return (
    <div style={{ marginBottom: 12, fontSize: 12.5 }}>
      <div style={{ color: 'var(--ink-3)', fontWeight: 600, marginBottom: 6 }}>Instagram post</div>
      {linked && !picking ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--ink)' }}>
            Linked {asset.instagram_kind ? asset.instagram_kind.toLowerCase() : 'post'}
          </span>
          {asset.instagram_synced_at && (
            <span style={{ color: 'var(--ink-4)' }}>· numbers from {fmtShort(asset.instagram_synced_at.slice(0, 10))}</span>
          )}
          {asset.proof_url && (
            <a href={asset.proof_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ink-3)' }}>
              open ↗
            </a>
          )}
          <Button type="button" variant="ghost" disabled={link.isPending} onClick={() => run(linked)}>
            {link.isPending ? 'Fetching…' : 'Refresh'}
          </Button>
          <Button type="button" variant="ghost" disabled={link.isPending} onClick={() => setPicking(true)}>
            Change
          </Button>
          <Button type="button" variant="ghost" disabled={link.isPending} onClick={() => run(null)}>
            Unlink
          </Button>
        </div>
      ) : !picking ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setKindFilter(asset.asset_type.includes('Story') ? 'Story' : '');
            setPicking(true);
          }}
        >
          Link to the Instagram post
        </Button>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="input" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} style={{ width: 'auto' }}>
              <option value="">All</option>
              <option value="Post">Posts</option>
              <option value="Carousel">Carousels</option>
              <option value="Reel">Reels</option>
              <option value="Story">Stories</option>
            </select>
            <Button type="button" variant="ghost" onClick={() => setPicking(false)}>Cancel</Button>
          </div>
          <div style={{
            marginTop: 6, maxHeight: 280, overflowY: 'auto',
            border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--bg-elevated)',
          }}>
            {list.isLoading && <div style={{ padding: 10, color: 'var(--ink-4)' }}>Loading Instagram…</div>}
            {notConnected && (
              <div style={{ padding: 10, color: 'var(--ink-4)' }}>
                Instagram isn't connected to mosy yet — Robbie needs to set up the Meta connection.
              </div>
            )}
            {list.error && !notConnected && (
              <div style={{ padding: 10, color: 'var(--ink-4)' }}>
                {errorDetail(list.error) ?? 'Instagram could not be reached.'}
              </div>
            )}
            {list.data && rows.length === 0 && (
              <div style={{ padding: 10, color: 'var(--ink-4)' }}>Nothing found.</div>
            )}
            {rows.map((m) => (
              <button
                key={m.id}
                type="button"
                disabled={link.isPending}
                onClick={() => run(m.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '6px 10px', border: 'none', borderBottom: '1px solid var(--border)',
                  background: m.id === linked ? 'var(--bg-subtle)' : 'transparent',
                  textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', fontSize: 12.5,
                }}
              >
                <Thumb m={m} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.caption || (m.kind === 'Story' ? 'Story' : 'No caption')}
                  </span>
                  <span style={{ color: 'var(--ink-4)' }}>
                    {m.kind} · {fmtWhen(m.timestamp)} {m.kind === 'Story' ? timeOf(m.timestamp) : ''}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      {message && <div style={{ marginTop: 6, color: 'var(--ink-3)' }}>{message}</div>}
    </div>
  );
};

export default InstagramLink;
